import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import type { Portal, ProvisioningStep, ProvisioningStepStatus } from "@/types";

interface ProvisioningStepperProps {
  portal: Portal;
  onRetry: () => void;
  isRetrying: boolean;
}

const STATUS_COLORS: Record<ProvisioningStepStatus, "default" | "success" | "error" | "info" | "warning"> = {
  pending: "default",
  in_progress: "info",
  completed: "success",
  failed: "error",
  skipped: "warning",
};

const STATUS_LABELS: Record<ProvisioningStepStatus, string> = {
  pending: "Pending",
  in_progress: "In Progress",
  completed: "Completed",
  failed: "Failed",
  skipped: "Skipped",
};

function getActiveStepIndex(steps: ProvisioningStep[]): number {
  for (let i = 0; i < steps.length; i++) {
    if (steps[i].status === "in_progress" || steps[i].status === "failed" || steps[i].status === "pending") {
      return i;
    }
  }
  return steps.length;
}

const ProvisioningStepper: React.FC<ProvisioningStepperProps> = ({
  portal,
  onRetry,
  isRetrying,
}) => {
  const steps = portal.provisioning_steps ?? [];
  const activeStep = getActiveStepIndex(steps);
  const hasFailed = steps.some((s) => s.status === "failed");
  const hasInProgress = steps.some((s) => s.status === "in_progress");

  return (
    <Box>
      {/* Portal summary */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Portal Information
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Slug
            </Typography>
            <Typography variant="body2" fontFamily="monospace">
              {portal.slug}
            </Typography>
          </Grid>
          {portal.contact_name && (
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Contact
              </Typography>
              <Typography variant="body2">{portal.contact_name}</Typography>
            </Grid>
          )}
          {portal.contact_email && (
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body2">{portal.contact_email}</Typography>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Created
            </Typography>
            <Typography variant="body2">
              {new Date(portal.created_at).toLocaleDateString()}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Provisioning progress */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6">
            Provisioning Progress
          </Typography>
          {hasInProgress && (
            <Chip label="In Progress" color="info" size="small" icon={<CircularProgress size={14} />} />
          )}
        </Box>

        {hasFailed && !hasInProgress && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={onRetry}
                disabled={isRetrying}
              >
                {isRetrying ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  "Retry Failed Steps"
                )}
              </Button>
            }
          >
            One or more steps failed. Click retry to re-attempt only the failed steps.
          </Alert>
        )}

        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step) => (
            <Step
              key={step.step}
              completed={step.status === "completed" || step.status === "skipped"}
            >
              <StepLabel
                error={step.status === "failed"}
                optional={
                  step.detail && (step.status === "completed" || step.status === "skipped") ? (
                    <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                      {step.detail}
                    </Typography>
                  ) : undefined
                }
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>{step.label}</span>
                  <Chip
                    label={STATUS_LABELS[step.status]}
                    color={STATUS_COLORS[step.status]}
                    size="small"
                    variant={step.status === "in_progress" ? "filled" : "outlined"}
                  />
                  {step.status === "in_progress" && <CircularProgress size={16} />}
                </Box>
              </StepLabel>
              <StepContent>
                {step.status === "failed" && step.error && (
                  <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
                    {step.error}
                  </Alert>
                )}
                {step.status === "in_progress" && (
                  <Typography variant="body2" color="text.secondary">
                    Setting up {step.label.toLowerCase()}...
                  </Typography>
                )}
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Paper>
    </Box>
  );
};

export default ProvisioningStepper;
